import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllBrands,
  getSingleBrandById,
  createBrand,
  updateBrand,
  deleteBrand,
} from "@/api/new-api/staff-management/menu-manage/essential-menu/brand";

// Thunks

export const fetchAllBrands = createAsyncThunk(
  "brands/fetchAll",
  async ({ restaurant_id, menu_id }, { rejectWithValue }) => {
    const res = await getAllBrands(restaurant_id, menu_id);
    if (res.status === "success") return res.data.brands;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleBrand = createAsyncThunk(
  "brands/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleBrandById(id);
    if (res.status === "success") return res.data.brand;
    return rejectWithValue(res.message);
  }
);

export const createNewBrand = createAsyncThunk(
  "brands/create",
  async (data, { rejectWithValue }) => {
    const res = await createBrand(data);
    if (res.status === "success") return res.data.brand;
    return rejectWithValue(res.message);
  }
);

export const updateExistingBrand = createAsyncThunk(
  "brands/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateBrand(id, data);
    if (res.status === "success") return res.data.brand;
    return rejectWithValue(res.message);
  }
);

export const deleteBrandById = createAsyncThunk(
  "brands/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteBrand(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);


// Initial State

const initialState = {
  brands: [],
  singleBrand: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const brandSlice = createSlice({
  name: "brands",
  initialState,
  reducers: {
    clearBrandMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllBrands.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllBrands.fulfilled, (state, action) => {
        state.getLoading = false;
        state.brands = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllBrands.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleBrand.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleBrand.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleBrand = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleBrand.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewBrand.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewBrand.fulfilled, (state, action) => {
        state.createLoading = false;
        state.brands.push(action.payload);
        state.success = "Brand created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewBrand.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingBrand.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingBrand.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.brands = state.brands.map((brand) =>
          brand.id === action.payload.id ? action.payload : brand
        );
        state.success = "Brand updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingBrand.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteBrandById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteBrandById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.brands = state.brands.map((brand) =>
          brand.id === action.payload ? { ...brand, status: "archive" } : brand
        );
        state.success = "Brand deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteBrandById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearBrandMessages } = brandSlice.actions;
export default brandSlice.reducer;
